/**
 * TrustRestrictionMatrix — single-glance view of which actions are
 * gated for the current member, why, and how to unblock them.
 *
 * Pure presentation. The DB still enforces the real gates via RLS &
 * RPC; this surface exists so members never hit a silent "nothing
 * happened" wall when they tap a restricted action.
 *
 * Mounted inside Account → Safety. Also re-usable on /safety.
 */
import { useMemo, useState } from "react";
import {
  ShieldCheck,
  ShieldAlert,
  Lock,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Info,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useTrustState, type RiskLevel } from "@/lib/trust/useTrustState";
import {
  TRUST_MATRIX,
  REMEDIATION,
  type ActionStatus,
  type MatrixRow,
} from "@/lib/trust/trustMatrix";

const STATUS_META: Record<
  ActionStatus,
  { label: string; tone: string; icon: typeof CheckCircle2 }
> = {
  allowed: {
    label: "Available",
    tone: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
    icon: CheckCircle2,
  },
  restricted: {
    label: "Limited",
    tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
    icon: AlertTriangle,
  },
  blocked: {
    label: "Paused",
    tone: "bg-destructive/10 text-destructive border-destructive/30",
    icon: Lock,
  },
};

const RISK_META: Record<
  RiskLevel,
  { label: string; tone: string; copy: string }
> = {
  low: {
    label: "Low",
    tone: "text-emerald-600 dark:text-emerald-400",
    copy: "No recent safety flags on your account.",
  },
  elevated: {
    label: "Elevated",
    tone: "text-amber-600 dark:text-amber-400",
    copy: "We've sent you a couple of nudges recently. Some actions are temporarily limited.",
  },
  high: {
    label: "High",
    tone: "text-destructive",
    copy: "Your account is under active review. Sensitive actions are paused until review completes.",
  },
};

const CATEGORY_LABELS: Record<MatrixRow["category"], string> = {
  messaging: "Messaging",
  matching: "Matching",
  events: "Events",
  voice_video: "Voice & video",
  discovery: "Discovery",
};

interface RowProps {
  row: MatrixRow;
  status: ActionStatus;
  reasons: ReturnType<MatrixRow["evaluate"]>["reasons"];
}

const MatrixRowItem = ({ row, status, reasons }: RowProps) => {
  const [open, setOpen] = useState(status !== "allowed");
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  const expandable = reasons.length > 0;

  return (
    <li className="rounded-xl border border-border bg-card/40">
      <button
        type="button"
        onClick={() => expandable && setOpen((v) => !v)}
        className="flex w-full items-start gap-3 p-4 text-left disabled:cursor-default"
        disabled={!expandable}
        aria-expanded={open}
      >
        <span
          className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${meta.tone}`}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium leading-tight">{row.label}</p>
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.tone}`}
            >
              {meta.label}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{row.description}</p>
        </div>
        {expandable && (
          <span className="mt-1 text-muted-foreground">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        )}
      </button>

      {expandable && open && (
        <div className="border-t border-border px-4 py-3">
          <ul className="space-y-2">
            {reasons.map((reason, idx) => {
              const remediation = reason.cta ? REMEDIATION[reason.cta] : null;
              return (
                <li key={idx} className="flex items-start gap-2.5 text-xs">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="leading-snug text-foreground/90">{reason.text}</p>
                    {remediation && (
                      <div className="mt-1.5">
                        {remediation.href ? (
                          <Button
                            asChild
                            size="sm"
                            variant="outline"
                            className="h-7 text-[11px]"
                          >
                            <Link to={remediation.href}>{remediation.label}</Link>
                          </Button>
                        ) : (
                          <span className="inline-flex items-center rounded-full border border-border bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                            {remediation.label} — see panel above
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </li>
  );
};

const TrustRestrictionMatrix = () => {
  const trust = useTrustState();

  const evaluated = useMemo(() => {
    return TRUST_MATRIX.map((row) => {
      const result = row.evaluate(trust);
      return { row, ...result };
    });
  }, [trust]);

  const grouped = useMemo(() => {
    const m = new Map<MatrixRow["category"], typeof evaluated>();
    for (const item of evaluated) {
      const list = m.get(item.row.category) ?? [];
      list.push(item);
      m.set(item.row.category, list);
    }
    return Array.from(m.entries());
  }, [evaluated]);

  const counts = useMemo(() => {
    const c = { allowed: 0, restricted: 0, blocked: 0 };
    for (const e of evaluated) c[e.status] += 1;
    return c;
  }, [evaluated]);

  const risk = RISK_META[trust.riskLevel];

  if (!trust.ready) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 animate-pulse">
        <div className="h-4 w-40 rounded bg-muted" />
        <div className="mt-4 h-2 w-full rounded bg-muted" />
        <div className="mt-6 space-y-2">
          <div className="h-12 rounded-xl bg-muted/60" />
          <div className="h-12 rounded-xl bg-muted/60" />
          <div className="h-12 rounded-xl bg-muted/60" />
        </div>
      </div>
    );
  }

  return (
    <section
      aria-labelledby="trust-matrix-heading"
      className="rounded-2xl border border-border bg-card p-6"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {trust.kycVerified ? (
              <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            )}
            <h3 id="trust-matrix-heading" className="text-sm font-semibold">
              What you can do right now
            </h3>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Some actions on Wavlynq unlock as your account becomes more trusted.
            Here's what's open, limited, or paused — and how to unlock the rest.
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Trust score
          </p>
          <p className="text-xl font-semibold tabular-nums">{trust.trustScore}</p>
        </div>
      </header>

      <div className="mt-4">
        <Progress value={trust.trustScore} className="h-1.5" />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2">
          <p className="text-lg font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
            {counts.allowed}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Available
          </p>
        </div>
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2">
          <p className="text-lg font-semibold tabular-nums text-amber-700 dark:text-amber-400">
            {counts.restricted}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Limited
          </p>
        </div>
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-2">
          <p className="text-lg font-semibold tabular-nums text-destructive">
            {counts.blocked}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Paused
          </p>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-border bg-secondary/40 p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium">Recent safety signals</p>
          <span className={`text-xs font-semibold ${risk.tone}`}>{risk.label}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{risk.copy}</p>
        {trust.riskRecentCount > 0 && (
          <p className="mt-1 text-[11px] text-muted-foreground">
            {trust.riskRecentCount} signal{trust.riskRecentCount === 1 ? "" : "s"} in the last 14 days.
          </p>
        )}
      </div>

      <div className="mt-5 space-y-5">
        {grouped.map(([category, items]) => (
          <div key={category}>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {CATEGORY_LABELS[category]}
            </p>
            <ul className="space-y-2">
              {items.map((item) => (
                <MatrixRowItem
                  key={item.row.id}
                  row={item.row}
                  status={item.status}
                  reasons={item.reasons}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>

      <p className="mt-5 text-[11px] text-muted-foreground">
        This summary is informational. Final access is enforced server-side and
        may differ if your account state changes.
      </p>
    </section>
  );
};

export default TrustRestrictionMatrix;
